<template>
  <div id="app" @click="handleGlobalClick" :data-theme="currentTheme">
    <nav class="navbar navbar-expand navbar-dark bg-dark">
      <img src="/jida.png" alt="即答" class="navbar-logo mr-2">
      <router-link to="/" class="navbar-brand" style="color:gray">即答公考，面试极好</router-link>
      <div class="navbar-nav mr-auto ml-4">
        <li class="nav-item">
          <router-link to="/aimode" class="nav-link" style="font-weight:600; color:white; font-size:1.25rem;">AI陪练</router-link>
        </li>
        <li class="nav-item">
          <a href="#" class="nav-link" style="font-weight:600; color:white; font-size:1.25rem;" @click.prevent="isLoggedIn ? $router.push('/pre-match') : showAuthModal = true">🔥匹配模式</a>
        </li>
        <li class="nav-item">
          <a href="#" class="nav-link" style="font-weight:600; color:white; font-size:1.25rem;" @click.prevent="isLoggedIn ? showInviteModal = true : showAuthModal = true">邀请有礼</a>
        </li>
        <li class="nav-item">
          <a href="#" class="nav-link vip-link" style="color:gray; font-size:1.25rem;" @click.prevent="isLoggedIn ? showVipModal = true : showAuthModal = true">
            <i class="fas fa-crown"></i> 开通VIP
          </a>
        </li>
      </div>
      <div class="navbar-nav ml-auto">
        <!-- Theme selector buttons -->
        <li class="nav-item theme-selector">
          <button class="theme-btn light" @click="changeTheme('light')" :class="{ active: currentTheme === 'light' }">
            <i class="fas fa-sun"></i>
          </button>
          <button class="theme-btn dark" @click="changeTheme('dark')" :class="{ active: currentTheme === 'dark' }">
            <i class="fas fa-moon"></i>
          </button>
        </li>
        <li class="nav-item">
          <a href="#" class="nav-link" @click.prevent="showFeedback">建议反馈</a>
        </li>
        <li class="nav-item" v-if="!isLoggedIn">
          <a href="#" class="nav-link" @click.prevent="showAuthModal = true">登录</a>
        </li>
        <li class="nav-item" v-if="isLoggedIn">
          <a href="#" class="nav-link" @click.prevent="logOut">登出</a>
        </li>
      </div>
    </nav>

    <div class="container mt-3">
      <router-view @show-auth="showAuthModal = true" />
    </div>
    
    <!-- 添加ICP备案信息 -->
    <footer class="icp-footer">
      <a href="https://beian.miit.gov.cn" target="_blank" rel="noopener noreferrer">
        粤ICP备2025392434号
      </a>
    </footer>
    
    <auth-modal 
      :show="showAuthModal" 
      @close="handleModalClose"
      @login-success="onLoginSuccess"
      @login-failed="resetInteraction"
    />
    
    <!-- VIP会员模态框 -->
    <vip-modal
      :show="showVipModal"
      @close="showVipModal = false"
    />
    
    <!-- 邀请码模态框 -->
    <div class="invitation-modal" v-if="showInviteModal">
      <div class="invitation-overlay" @click="showInviteModal = false"></div>
      <div class="invitation-container">
        <div class="invitation-header">
          <h3>邀请有礼</h3>
          <button class="invitation-close" @click="showInviteModal = false">×</button>
        </div>
        
        <div class="invitation-body">
          <p class="invitation-description">邀请好友加入即答，让大家一起备战面试！</p>
          
          <div v-if="inviteLoading" class="invitation-loading">
            <div class="spinner"></div>
            <span>获取邀请码中...</span>
          </div>
          
          <div v-else-if="inviteError" class="invitation-error">
            <p>{{ inviteError }}</p>
            <button class="btn-retry" @click="generateInviteCode">重试</button>
          </div>
          
          <div v-else-if="inviteCode" class="invitation-content">
            <div class="invite-code-wrapper">
              <h4>您的专属邀请码</h4>
              <div class="invite-code-display">{{ inviteCode }}</div>
              <div class="invite-actions">
                <button class="btn-copy" @click="copyInviteCode">
                  <i class="fas fa-copy"></i> 复制
                </button>
              </div>
            </div>
            
            <div class="invitation-info">
              <div class="info-item">
                <span class="info-label">已邀请人数:</span>
                <span class="info-value">{{ totalInvites || 0 }}</span>
              </div>
              <div class="info-item" v-if="inviteExpires">
                <span class="info-label">有效期至:</span>
                <span class="info-value">{{ formatDate(inviteExpires) }}</span>
              </div>
            </div>
            
            <div class="invitation-tips">
              <h4>邀请方式</h4>
              <ol>
                <li>点击"复制"按钮复制您的邀请码</li>
                <li>将邀请码分享给好友</li>
                <li>好友注册时填写您的邀请码即可</li>
              </ol>
            </div>
          </div>
          
          <div v-else class="invitation-content">
            <p>您还没有邀请码，点击下方按钮生成一个邀请码。</p>
            <button class="btn-generate" @click="generateInviteCode">生成邀请码</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import AuthModal from './components/AuthModal.vue';
import VipModal from './components/VipModal.vue';
import { mapGetters } from 'vuex';

export default {
  name: 'App',
  components: {
    AuthModal,
    VipModal
  },
  data() {
    return {
      showAuthModal: false,
      showInviteModal: false,
      showVipModal: false,
      inviteCode: '',
      inviteExpires: null,
      maxUses: 0,
      currentUses: 0,
      totalInvites: 0,
      inviteLoading: false,
      inviteError: null,
      hasInteracted: false, // 标记用户是否与页面交互过
      authCheckCompleted: false // 标记认证检查是否完成
    };
  },
  computed: {
    ...mapGetters(['isLoggedIn', 'currentUser', 'currentTheme'])
  },
  watch: {
    showInviteModal(val) {
      if (val && this.isLoggedIn) {
        this.checkInviteCode();
      }
    }
  },
  mounted() {
    // 检查用户是否已登录，避免在已登录状态下显示模态窗口
    this.authCheckCompleted = true;
    
    // 应用保存的主题
    document.body.setAttribute('data-theme', this.currentTheme);
    
    // 监听显示VIP模态框的事件
    window.addEventListener('show-vip-modal', () => {
      this.showVipModal = true;
    });
  },
  methods: {
    // 检查用户是否已有邀请码
    async checkInviteCode() {
      if (!this.isLoggedIn) return;
      
      this.inviteLoading = true;
      this.inviteError = null;
      
      try {
        console.log('开始检查邀请码...');
        const response = await axios.get('/invitation/check', {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
        console.log('邀请码检查响应:', response.data);
        
        if (response.data.success) {
          if (response.data.hasCode) {
            this.inviteCode = response.data.code;
            this.maxUses = response.data.maxUses;
            this.currentUses = response.data.currentUses;
            this.inviteExpires = response.data.expiresAt ? new Date(response.data.expiresAt) : null;
          } else {
            this.inviteCode = '';
          }
          this.totalInvites = response.data.totalInvites || 0;
        } else {
          this.inviteError = response.data.message || '获取邀请码信息失败';
        }
      } catch (error) {
        console.error('检查邀请码出错:', error);
        if (error.response) {
          console.error('错误响应:', error.response.data);
          this.inviteError = error.response.data.message || '获取邀请码信息失败';
        } else if (error.request) {
          console.error('请求未收到响应:', error.request);
          this.inviteError = '网络错误，请稍后再试';
        } else {
          console.error('请求配置错误:', error.message);
          this.inviteError = '请求配置错误，请稍后再试';
        }
      } finally {
        this.inviteLoading = false;
      }
    },
    
    // 生成邀请码
    async generateInviteCode() {
      if (!this.isLoggedIn) return;
      
      this.inviteLoading = true;
      this.inviteError = null;
      
      try {
        console.log('开始生成邀请码...');
        const response = await axios.get('/invitation/generate', {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
        console.log('生成邀请码响应:', response.data);
        
        if (response.data.success) {
          this.inviteCode = response.data.code;
          // 重新获取完整的邀请码信息
          await this.checkInviteCode();
        } else {
          this.inviteError = response.data.message || '生成邀请码失败';
        }
      } catch (error) {
        console.error('生成邀请码出错:', error);
        if (error.response) {
          console.error('错误响应:', error.response.data);
          this.inviteError = error.response.data.message || '生成邀请码失败';
        } else if (error.request) {
          console.error('请求未收到响应:', error.request);
          this.inviteError = '网络错误，请稍后再试';
        } else {
          console.error('请求配置错误:', error.message);
          this.inviteError = '请求配置错误，请稍后再试';
        }
      } finally {
        this.inviteLoading = false;
      }
    },
    
    // 复制邀请码到剪贴板
    copyInviteCode() {
      if (!this.inviteCode) return;
      
      // 创建临时文本区域
      const textArea = document.createElement('textarea');
      textArea.value = this.inviteCode;
      document.body.appendChild(textArea);
      
      // 选择文本并复制
      textArea.select();
      document.execCommand('copy');
      
      // 移除临时元素
      document.body.removeChild(textArea);
      
      // 提示用户已复制
      alert('邀请码已复制到剪贴板');
    },
    
    // 格式化日期
    formatDate(date) {
      if (!date) return '';
      
      const d = new Date(date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    },
    
    handleGlobalClick(event) {
      // 防止模态窗口已经显示的情况下再次触发
      if (this.showAuthModal) return;
      
      // 忽略导航栏中的点击事件
      if (event.target.closest('.navbar')) return;
      
      // 忽略链接点击事件，避免与路由冲突
      if (event.target.tagName === 'A' || event.target.closest('a')) return;
      
      // 仅在未登录状态且完成认证检查后显示模态窗口
      if (!this.isLoggedIn && this.authCheckCompleted && !this.hasInteracted) {
        this.showAuthModal = true;
        this.hasInteracted = true; // 标记用户已经交互过，防止重复弹窗
      }
    },
    logOut() {
      this.$store.dispatch('logout');
      this.$router.push('/');
      // 重置交互标记，使得在登出后点击可以再次触发登录窗口
      this.hasInteracted = false;
    },
    onLoginSuccess() {
      // 在登录成功后刷新当前路由
      window.location.reload();
    },
    handleModalClose() {
      this.showAuthModal = false;
      
      // 如果用户关闭模态窗口但仍未登录，重置交互标志，以便再次点击时弹出登录窗口
      if (!this.isLoggedIn) {
        this.resetInteraction();
      }
    },
    
    resetInteraction() {
      // 重置交互标志，允许再次弹出登录窗口
      this.hasInteracted = false;
    },
    
    changeTheme(theme) {
      this.$store.dispatch('changeTheme', theme);
    },
    
    showFeedback() {
      alert('请发送反馈至: jida.chat@139.com');
    }
  }
};
</script>

<style>
/* 全局样式 */
* {
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
}

html, body {
  margin: 0;
  padding: 0;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  font-size: 16px;
  line-height: 1.5;
  color: #333;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
}

#app {
  width: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
}

/* 移动端优化 */
@media (max-width: 768px) {
  html {
    font-size: 14px; /* 移动端基础字体稍小 */
  }
  
  input, button, select, textarea {
    font-size: 16px !important; /* 防止iOS自动缩放 */
  }
  
  /* 改善移动端点击响应区域 */
  button, 
  [role="button"], 
  input[type="button"], 
  input[type="submit"], 
  input[type="reset"] {
    min-height: 44px;
    min-width: 44px;
  }
  
  /* 增强移动端滚动体验 */
  .scrollable-container {
    -webkit-overflow-scrolling: touch;
  }
}

/* 适配不同设备像素比 */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  /* 优化高分辨率显示屏上的1px边框 */
  .hairline-border {
    position: relative;
  }
  .hairline-border::after {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    width: 200%;
    height: 200%;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: inherit;
    transform: scale(0.5);
    transform-origin: 0 0;
    pointer-events: none;
  }
}

/* 适配深色模式 */
@media (prefers-color-scheme: dark) {
  .app-dark-mode-support {
    --text-color: #f1f1f1;
    --bg-color: #121212;
    --surface-color: #1e1e1e;
  }
}

/* 移动端导航适配 */
.mobile-nav-container {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background: #fff;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  display: none;
}

@media (max-width: 768px) {
  .mobile-nav-container {
    display: flex;
  }
  
  .desktop-only {
    display: none !important;
  }
  
  .mobile-only {
    display: block !important;
  }
  
  /* 适配底部导航空间 */
  .has-bottom-nav {
    padding-bottom: 60px;
  }
}

/* 避免iOS中输入时的页面缩放问题 */
@supports (-webkit-touch-callout: none) {
  body {
    /* iOS对于小于16px的输入框会自动放大 */
    font-size: 16px;
  }
}

/* 页面整体样式 */
body {
  background: radial-gradient(circle at 10% 20%, rgba(99,102,241,0.05) 0%, rgba(255,255,255,1) 100%);
  min-height: 100vh;
  margin: 0;
}

#app {
  font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #1e293b;
}

.navbar-brand {
  font-weight: 600;
}

.navbar {
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

/* Logo in the navbar */
.navbar-logo {
  height: 30px;
  width: auto;
  margin-right: 10px;
}

/* Theme selector styles */
.theme-selector {
  display: flex;
  align-items: center;
  margin-right: 15px;
}

.theme-btn {
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 5px 10px;
  margin: 0 2px;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.theme-btn.active {
  background: rgba(255, 255, 255, 0.2);
}

.theme-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* Dark theme styles */
[data-theme="dark"] {
  background: #121212;
  color: #f8f9fa;
}

[data-theme="dark"] .container {
  background: #1e1e1e;
  color: #f8f9fa;
}

[data-theme="dark"] .card {
  background: #2d2d2d;
  color: #f8f9fa;
  border-color: #444;
}

[data-theme="dark"] .card-header {
  background: #383838;
  border-color: #444;
}

[data-theme="dark"] .form-control {
  background: #2d2d2d;
  color: #f8f9fa;
  border-color: #444;
}

[data-theme="dark"] .form-control:focus {
  background: #2d2d2d;
  color: #f8f9fa;
}

[data-theme="dark"] .btn-secondary {
  background-color: #495057;
  border-color: #495057;
}

[data-theme="dark"] .text-muted {
  color: #adb5bd !important;
}

[data-theme="dark"] .table {
  color: #f8f9fa;
}

[data-theme="dark"] .table th,
[data-theme="dark"] .table td {
  border-color: #444;
}

/* Keep the navbar dark in both themes */
.navbar-dark {
  background-color: #343a40 !important;
}

/* 邀请码模态框样式 */
.invitation-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.invitation-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(3px);
}

.invitation-container {
  position: relative;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  width: 90%;
  max-width: 420px;
  z-index: 1;
  overflow: hidden;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.invitation-header {
  padding: 16px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.invitation-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.invitation-close {
  background: transparent;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  opacity: 0.8;
  transition: opacity 0.2s;
}

.invitation-close:hover {
  opacity: 1;
}

.invitation-body {
  padding: 20px;
}

.invitation-description {
  text-align: center;
  color: #4b5563;
  margin-bottom: 20px;
}

.invitation-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px 0;
}

.invitation-loading .spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #6366f1;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.invitation-error {
  text-align: center;
  padding: 20px 0;
}

.invitation-error p {
  color: #ef4444;
  margin-bottom: 15px;
}

.btn-retry {
  background: #f3f4f6;
  color: #4b5563;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-retry:hover {
  background: #e5e7eb;
}

.invitation-content {
  padding: 10px 0;
}

.invite-code-wrapper {
  background: #f9fafb;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
  text-align: center;
}

.invite-code-wrapper h4 {
  margin: 0 0 10px 0;
  color: #4b5563;
  font-size: 16px;
}

.invite-code-display {
  font-size: 24px;
  font-weight: 600;
  letter-spacing: 1px;
  color: #6366f1;
  background: white;
  border: 1px dashed #d1d5db;
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 15px;
  user-select: all;
}

.invite-actions {
  display: flex;
  justify-content: center;
}

.btn-copy, .btn-generate {
  background: #6366f1;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-copy:hover, .btn-generate:hover {
  background: #4f46e5;
}

.invitation-info {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 20px;
}

.info-item {
  flex: 1;
  min-width: 120px;
  background: #f3f4f6;
  padding: 10px;
  border-radius: 6px;
  text-align: center;
}

.info-label {
  display: block;
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 5px;
}

.info-value {
  font-weight: 600;
  color: #4b5563;
  font-size: 16px;
}

.invitation-tips {
  background: #f9fafb;
  border-radius: 8px;
  padding: 15px;
}

.invitation-tips h4 {
  margin: 0 0 10px 0;
  color: #4b5563;
  font-size: 16px;
}

.invitation-tips ol {
  margin: 0;
  padding-left: 20px;
}

.invitation-tips li {
  color: #6b7280;
  margin-bottom: 5px;
}

@media (max-width: 480px) {
  .invitation-container {
    max-width: 95%;
  }
  
  .invitation-body {
    padding: 15px;
  }
  
  .invite-code-display {
    font-size: 20px;
    letter-spacing: 0.5px;
  }
}

/* VIP link styling */
.vip-link {
  color: #f8d62b !important;
  font-weight: bold;
  display: flex;
  align-items: center;
}

.vip-link i {
  margin-right: 0.25rem;
}

.vip-link:hover {
  color: #ffea8a !important;
}

/* ICP备案号样式 */
.icp-footer {
  width: 100%;
  text-align: center;
  padding: 15px 0;
  margin-top: 10px;
  font-size: 14px;
  color: #666;
  background-color: #f8f9fa;
}

.icp-footer a {
  color: #666;
  text-decoration: none;
}

.icp-footer a:hover {
  color: #333;
  text-decoration: underline;
}

/* 深色模式下的ICP备案号样式 */
[data-theme="dark"] .icp-footer {
  background-color: #343a40;
}

[data-theme="dark"] .icp-footer a {
  color: #adb5bd;
}

[data-theme="dark"] .icp-footer a:hover {
  color: #ced4da;
}
</style>
